import {
  AnimationConfig,
  AnimationEffects,
  AnimationEffectValue,
  Effects,
  FadeMethod,
  MoveMethod,
} from "../types";
import { DEFAULT_ANIMATION_DURATION_SECONDS, VIDEO_FPS } from "../constants";

export const isEffectActive = (
  value: AnimationEffectValue | undefined
): boolean => {
  if (value === undefined) return false;
  if (typeof value === "boolean") return value;
  return value.enabled;
};

export const getEffectDuration = (
  value: AnimationEffectValue | undefined
): number => {
  if (!value || typeof value === "boolean")
    return DEFAULT_ANIMATION_DURATION_SECONDS;
  return value.duration ?? DEFAULT_ANIMATION_DURATION_SECONDS;
};

export const getEffectDurationFrames = (
  value: AnimationEffectValue | undefined
): number => {
  return Math.round(getEffectDuration(value) * VIDEO_FPS);
};

/**
 * Returns the user-configured delay (in seconds) before the effect starts.
 * Defaults to 0 — intended for UI/preview consumers where "no delay set"
 * means "start immediately".
 */
export const getEffectDelay = (
  value: AnimationEffectValue | undefined
): number => {
  if (!value || typeof value === "boolean") return 0;
  return value.delay ?? 0;
};

/**
 * Returns the effect's start frame for server-template generation.
 *
 * When `delay` is set, returns `delay * VIDEO_FPS` (positive frames from
 * the start of the timeline). When unset, returns `fallback` — callers pass
 * `0` for entrance effects (start at frame 0) and `-duration` for exit
 * effects (server convention: negative start anchors the animation to the
 * end of the timeline).
 */
export const getEffectDelayFrames = (
  value: AnimationEffectValue | undefined,
  fallback: number
): number => {
  if (!value || typeof value === "boolean") return fallback;
  if (value.delay === undefined) return fallback;
  return Math.round(value.delay * VIDEO_FPS);
};

export const getEffectMethod = (
  value: AnimationEffectValue | undefined
): MoveMethod | FadeMethod => {
  if (!value || typeof value === "boolean") return MoveMethod.EaseInOut;
  return value.method ?? MoveMethod.EaseInOut;
};

export const normalizeAnimationConfig = (
  value: AnimationEffectValue | undefined
): AnimationConfig | null => {
  if (value === undefined) return null;
  if (typeof value === "boolean") {
    return value
      ? {
          enabled: true,
          duration: DEFAULT_ANIMATION_DURATION_SECONDS,
          method: MoveMethod.EaseInOut,
        }
      : null;
  }
  return value.enabled ? value : null;
};

const FADE_METHODS = new Set<string>(Object.values(FadeMethod));

export const toFadeMethod = (method: MoveMethod | FadeMethod): FadeMethod => {
  return FADE_METHODS.has(method)
    ? (method as FadeMethod)
    : FadeMethod.EaseInOut;
};

export const isFadeEffect = (
  effect: Effects
): effect is Effects.fadeIn | Effects.fadeOut => {
  return effect === Effects.fadeIn || effect === Effects.fadeOut;
};

export const isEnterFromEffect = (effect: Effects): boolean => {
  return [
    Effects.enterFromTop,
    Effects.enterFromBottom,
    Effects.enterFromLeft,
    Effects.enterFromRight,
  ].includes(effect);
};

export const isLeaveToEffect = (effect: Effects): boolean => {
  return [
    Effects.leaveToTop,
    Effects.leaveToBottom,
    Effects.leaveToLeft,
    Effects.leaveToRight,
  ].includes(effect);
};

export const isExitEffect = (effect: Effects): boolean => {
  return effect === Effects.fadeOut || isLeaveToEffect(effect);
};

export type EffectGroup = "fadeIn" | "fadeOut" | "enterFrom" | "leaveTo";

export const getEffectGroup = (effect: Effects): EffectGroup => {
  if (effect === Effects.fadeIn) return "fadeIn";
  if (effect === Effects.fadeOut) return "fadeOut";
  if (isLeaveToEffect(effect)) return "leaveTo";
  return "enterFrom";
};

export const getActiveEffects = (animations?: AnimationEffects): Effects[] => {
  if (!animations) return [];
  return Object.values(Effects).filter((effect) =>
    isEffectActive(animations[effect])
  );
};

/** @deprecated Use getActiveEffects() for multi-animation support */
export const getActiveEffect = (
  animations?: AnimationEffects
): Effects | null => {
  const effects = getActiveEffects(animations);
  return effects.length > 0 ? (effects[0] ?? null) : null;
};
